import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import {
  AuthManagerService,
  AuthParams,
  UserAuthenticationType,
} from 'src/app/services/auth-manager/auth-manager.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ErrorStrings } from 'src/app/constants/form.constants';
import { Alert } from 'src/app/interfaces/alert';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import {
  RedirectionParams,
  RedirectionService,
} from 'src/app/services/redirection/redirection.service';
import { PetClaimingInfo, PetService } from 'src/app/services/pet/pet.service';
import { ACCOUNT_TYPE_PREVIEW_ROUTE } from 'src/app/constants/route.constants';
import { COMPANY_PHONE_NUMBER } from 'src/app/constants/app.constants';
import { UserService } from 'src/app/services/user/user.service';
import { ChipService } from 'src/app/services/chip/chip.service';
import { User } from 'src/app/interfaces/user';
import { RegistrationProgressService } from 'src/app/services/registration-progress/registration-progress.service';
import { isPlatformBrowser } from '@angular/common';

export enum EmailOrPhoneNumberStatus {
  Default,
  HasEmailOrPhoneNumber,
  DontHaveEmailOrPhoneNumber
}

export enum ChipNumberStatus {
  Default,
  HasChipNumber,
  DontHaveChipNumber
}

export enum UserIdentifierType {
  Id,
  EmailAddress,
  PhoneNumber,
  ChipNumber
}

@Component({
  selector: 'app-login-register',
  templateUrl: './login-register.component.html',
  styleUrls: ['./login-register.component.scss'],
})
export class LoginRegisterComponent implements OnInit {
  loading = false;
  alert: Alert = null;
  errorStrings = ErrorStrings;
  adminLoginUrl = environment.adminLoginUrl;
  redirectTimer = 0;
  redirectParams?: RedirectionParams;
  hidePassword: boolean = true;
  user: User;

  authWithChipNumber: boolean = true;
  hasChipNumberStatus: ChipNumberStatus = ChipNumberStatus.Default;
  partialEmail: string = '';
  notValidChipNumber: boolean = false;
  inputColor: string = '';
  chipNumberInputData: string = '';
  chipNotImplantedError: string = 'chip_not_implanted';
  chipNotFoundError: string = 'chip_not_found';

  hasEmailOrPhoneNumberStatus: EmailOrPhoneNumberStatus = EmailOrPhoneNumberStatus.Default;
  userIdentifier: UserIdentifierType = UserIdentifierType.EmailAddress;
  emailAndPhoneNumberInputData: string = '';
  inputColorForEmailAndPhoneNumber: string = '';
  validEmail: boolean = false;

  isHovered: boolean = false;
  isBrowser: boolean;

  model: AuthParams = {
    emailAddress: '',
    password: '',
    authenticationType: UserAuthenticationType.Email,
  }
  petClaimModel: PetClaimingInfo = {
    chipNumber: '',
    vetCode: '',
    vetCodeSkipped: true,
  };
 
  constructor (
    public authManager: AuthManagerService,
    private router: Router,
    private route: ActivatedRoute,
    private redirectionService: RedirectionService,
    private userService: UserService,
    private chipService: ChipService,
    private registrationProgressService: RegistrationProgressService,
    private petService: PetService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.user = this.route.snapshot.data.user as User;
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  private inputSubject = new Subject<string>();

  public onAuthWithChipNumberChanged(data: number) {
    if (data === 2) {
      this.authWithChipNumber = false;
      return;
    }
    this.authWithChipNumber = true;
    return;
  }

  public redirectToTransferOfKeepership() {
    const base64Encoded = btoa(this.chipNumberInputData);
    if (this.isBrowser) {
      // store chip number in localStorage on ToK button click
      localStorage.setItem('enteredChipNumberToK', base64Encoded);
    }
    this.router.navigate(['/claim-pet'], { queryParams: { chipNumber: base64Encoded } });
  }

  public redirectToRecoverAccount() {
    this.router.navigate(['/forgot-password'], { queryParams: { email: this.emailAndPhoneNumberInputData } });
  }

  public redirectToLogIn() {
    if(this.userService.options.lastUrlRequested !== '/login-register') {
      this.router.navigate(['/login'], { queryParams: { r: this.userService.options.lastUrlRequested }});
      return;
    }
    this.router.navigate(['/login']);
    return;
  }

  public redirectToRegister() {
    if (this.validEmail) {
      const base64Encoded = btoa(this.emailAndPhoneNumberInputData);
      this.router.navigate(['/register'], { queryParams: { email: base64Encoded } });
      return;
    }
    this.router.navigate(['/register']);
    return;
  }

  public onInputChange(value: string): void {

    if (value.length < 15) {
      this.hasChipNumberStatus = ChipNumberStatus.Default;
      this.chipNumberInputData = '';
    }
    const numericValue = parseInt(value, 10);
    if (isNaN(numericValue) || (value.length !== 15)) {
      this.notValidChipNumber = true;
      this.inputColor = '';
      return;
    }
    this.chipNumberInputData = value;
    this.notValidChipNumber = false;
    this.inputSubject.next(value);
    return
  }

  public onInputChangeForEmailOrPhone(value: string): void {
    const isEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
    const isPhoneNumber = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,11}$/.test(value);

    if (value.length === 0) {
      this.hasEmailOrPhoneNumberStatus = EmailOrPhoneNumberStatus.Default
    }

    if (isEmail) {
      this.validEmail = true;
      this.userIdentifier = UserIdentifierType.EmailAddress;
      this.model.emailAddress = value
    } else if (isPhoneNumber) {
      this.validEmail = true;
      this.userIdentifier = UserIdentifierType.PhoneNumber;
      this.model.emailAddress = value
    }

    if (!isEmail && !isPhoneNumber) {
      this.validEmail = false;
      this.inputColorForEmailAndPhoneNumber = '';
      return;
    }
  
    this.notValidChipNumber = false;
    this.inputSubject.next(value);
  }

  onIconHover(isHovered: boolean): void {
    this.isHovered = isHovered;
  }

  checkEmailDbClick() {
    this.closeAlert();
    this.loading = true;
    this.userService.checkEmailOrPhoneNumber(this.emailAndPhoneNumberInputData, this.userIdentifier).subscribe(
      () => {
        this.hasEmailOrPhoneNumberStatus = EmailOrPhoneNumberStatus.HasEmailOrPhoneNumber
        this.inputColorForEmailAndPhoneNumber = '#07a550';
      },
      (err) => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 404) {
            this.hasEmailOrPhoneNumberStatus = EmailOrPhoneNumberStatus.DontHaveEmailOrPhoneNumber
            this.inputColorForEmailAndPhoneNumber = '#ee7203';
          }
          if (err.status === 401) {
            this.hasEmailOrPhoneNumberStatus = EmailOrPhoneNumberStatus.DontHaveEmailOrPhoneNumber
            this.inputColorForEmailAndPhoneNumber = '#ee7203';
          }
        } else {
          this.alert = {
            type: 'danger',
            message: 'An error has occurred. Please try again later.',
          };
        }
      }
    )
    .add(() => {
      this.loading = false;
    });
  }

  checkChipDbClick() {
    this.closeAlert();
    this.loading = true;
    this.chipService.getUserEmailForChipNumber(this.chipNumberInputData).subscribe(
      (response) => {
        this.partialEmail = response.body
        this.hasChipNumberStatus = ChipNumberStatus.HasChipNumber
        this.inputColor = '#07a550';
        this.petClaimModel.chipNumber = this.chipNumberInputData;
      },
      (err) => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 404) {
            this.hasChipNumberStatus = ChipNumberStatus.DontHaveChipNumber
            this.inputColor = '#ee7203';
            const errorMessage = JSON.parse(err.error)
            if (errorMessage.error_code === this.chipNotImplantedError) {
              this.petClaimModel.chipNumber = this.chipNumberInputData;
              if (this.isBrowser) {
                localStorage.setItem('enteredChipNumber', btoa(this.chipNumberInputData));
              }
            }
            if (errorMessage.error_code === this.chipNotFoundError) {
              this.notValidChipNumber = true;
              this.alert = {
                type: 'danger',
                message: 'The chip number could not be found. Please double-check to ensure that you have entered the number correctly!',
              };
            }
          }
          if (err.status === 401) {
            this.hasChipNumberStatus = ChipNumberStatus.DontHaveChipNumber
            this.inputColor = '#ee7203';
          }
        } else {
          this.alert = {
            type: 'danger',
            message: 'An error has occurred. Please try again later.',
          };
        }
      }
    )
    .add(() => {
      this.loading = false;
    });
  }
  
  ngOnInit(): void {
    if (this.authManager.isAuthenticated) {
      this.userService.getMe().subscribe((userResponse: User) => {
        this.redirectUserOrNavigateToDashboard(userResponse);
      });
    }

    // Prefill email address if "email" is passed as query param
    // (this is set when a redirection from the signup page - 409 status code)
    if (this.route.snapshot.queryParams.email) {
      this.model.emailAddress = this.route.snapshot.queryParams.email;
      this.alert = {
        type: 'success',
        message: `It looks like you're already registered. Please enter your password to log in.`,
        bold: true,
      }
    }

    if (this.route.snapshot.queryParams.chip) {
      this.model.emailAddress = this.route.snapshot.queryParams.chip;
      this.authWithChipNumber = true;
      this.alert = {
        type: 'success',
        message: `It looks like chip number ${this.route.snapshot.queryParams.chip} is already registered. Please enter your password to log in.`,
        bold: true,
      }
    }

    // Setting redirection to be used when after login we want the user redirected to a specific page
    this.redirectParams = this.redirectionService.getParams(
      this.route.snapshot.queryParams.r
    );
  }

  login() {
    this.closeAlert();
    this.loading = true;
    this.trackEvent('login::clicked');
    this.model.authenticationType = this.authWithChipNumber
      ? UserAuthenticationType.ChipNumber
      : this.model.emailAddress.includes('@')
      ? UserAuthenticationType.Email
      : UserAuthenticationType.PhoneNumber;
    this.authManager
      .login(this.model)
      .subscribe(
        (user) => {
          if (!user) {
            this.redirectToAdmin();
            return;
          }
          // if the user comes from the account-type-preview page we want to redirect him to the dashboard
          if (
            this.redirectParams?.l &&
            !this.redirectParams.l.includes(`/${ACCOUNT_TYPE_PREVIEW_ROUTE}`)
          ) {
            this.router.navigate(this.redirectParams.l, {
              queryParams: this.redirectParams.q,
              fragment: this.redirectParams.f,
            });
          } else {
            this.redirectUserOrNavigateToDashboard(user);
          }
        },
        (err) => {
          if (err instanceof HttpErrorResponse && err.status === 400) {
            if (
              err.error &&
              err.error.error_code === 'multiple_users_with_phone'
            ) {
              this.alert = {
                type: 'danger',
                message: err.error.error_message,
              };
            } else {
              this.alert = {
                type: 'danger',
                message:
                  'Please enter a valid email address, phone number or chip number and a password.',
              };
            }
          } else if (err instanceof HttpErrorResponse && err.status === 401) {
            if (err.error && err.error.error_code === 'email_not_found') {
              // User not yet registered: Redirect to the registration form
              this.router.navigate(['/register'], {
                queryParams: { email: this.model.emailAddress },
              });
            } else if (
              err.error &&
              (err.error.error_code === 'phone_number_not_found' ||
                err.error.error_code === 'chip_not_found')
            ) {
              this.alert = {
                type: 'danger',
                message: err.error.error_message,
              };
            } else if (
              err.error &&
              (err.error.error_code === 'invalid_password')
            ) {
              this.alert = {
                type: 'danger',
                message: "The password is incorrect.",
              };
            } else if (
              err.error &&
              err.error.error_code === 'password_not_set'
            ) {
              // Legacy user with password not set
              this.router.navigate(['/forgot-password'], {
                queryParams: {
                  email: this.model.emailAddress,
                  type: 'passnotset',
                },
              });
            } else if (
              err.error &&
              err.error.error_code === 'wrong_account_type'
            ) {
              // Admin users trying to login exception
              this.redirectToAdmin();
            } else if (err.error && err.error.error_code === 'inactive_user') {
              this.alert = {
                type: 'danger',
                message: `Please contact identibase on ${COMPANY_PHONE_NUMBER}.`,
              };
            } else {
              this.alert = {
                type: 'danger',
                message:
                  this.authWithChipNumber === true
                    ? 'The chip number or password is incorrect.'
                    : 'The email address or password is incorrect.',
              };
            }
          } else {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
          }
        }
      )
      .add(() => {
        this.loading = false;
      });
  }

  next(): void {
    this.trackEvent('registerchip::chipvetcode::submitted', this.petClaimModel);
    this.closeAlert();
    this.loading = true;
    this.petService
      .claimPet(this.petClaimModel)
      .subscribe(
        (pet) => {
          const queryParam = this.route.snapshot.queryParams;
          if (queryParam.r) {
            this.router.navigate(['/my-pet', pet.id, 'confirm'], {
              queryParams: { r: queryParam.r },
            });
          } else {
            this.router.navigate(['/my-pet', pet.id, 'confirm']);
          }
        },
        (err) => {
          if (err instanceof HttpErrorResponse && err.status === 400) {
            if (err.error && err.error.error_code === 'already_claimed_me') {
              this.alreadyClaimedByMeRedirection();
            } else {
              this.alert = {
                type: 'danger',
                message:
                  "Couldn't complete the request. Please check if the information entered are valid and try again.",
              };
            }
          } else {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
          }
        }
      )
      .add(() => {
        this.loading = false;
      });
  }

  closeAlert() {
    this.alert = null;
  }

  trackEvent(event: string, data?: any) {
    if (!this.isBrowser) return;
    window.analytics.track(event, data);
  }

  private redirectToAdmin(): void {
    this.redirectTimer = 3;
    this.showRedirectToAdminMessage();
    if (this.isBrowser) {
      const timer = window.setInterval(() => {
        this.redirectTimer--;
        this.showRedirectToAdminMessage();
        if (!this.redirectTimer) {
          window.clearInterval(timer);
          window.location.href = this.adminLoginUrl;
        }
      }, 1000);
    }
  }

  private redirectUserOrNavigateToDashboard(user: User) {
    const redirectToTheB21KFlow = user.tokRequests.length > 0;
    this.redirectParams = this.redirectionService.getParams(
      this.route.snapshot.queryParams.r
    );
    
    if (!this.authWithChipNumber && !this.notValidChipNumber && this.petClaimModel.chipNumber.length !== 0) {
      this.next()
    }

    if (this.redirectParams?.l) {
      this.router.navigate(this.redirectParams.l, {
        queryParams: this.redirectParams.q,
        fragment: this.redirectParams.f,
      });
    } else {
      // If the user has not completed the registration process yet, redirect them to stage1 of the registration process
      const progressData =
        this.registrationProgressService.getRegistrationProgressData(user);
      if (
        !progressData.isFullNameRegistrationCompleted ||
        !progressData.isMembershipSelectionCompleted ||
        !progressData.isPetChipNumberRegistrationCompleted ||
        !progressData.isWelcomePackSelectCompleted
      ) {
        if (redirectToTheB21KFlow) {
          this.router.navigate(['/tok-details']);
          return;
        } else {
          this.router.navigate(['/account/owner-details']);
          return;
        }
      }
      this.router.navigate(['/dashboard']);
    }
  }

  private alreadyClaimedByMeRedirection(): void {
    const claimedPet = this.user.pets.find(
      (pet) => pet.chipNumber === this.petClaimModel.chipNumber.replace(/\s/g, '')
    );
    if (claimedPet) {
      this.router.navigate(['/my-pet', claimedPet.id]);
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  private showRedirectToAdminMessage(): void {
    this.alert = {
      type: 'danger',
      message: `It looks like you're not a keeper. You will be redirected automatically in <strong>${this.redirectTimer}s</strong>. If you don't want to wait, please <a href="${this.adminLoginUrl}" rel="nofollow">log in here</a>.`,
    };
  }
};
